import React from 'react';
import { Briefcase, Globe, Linkedin, MapPin, StickyNote, Home as HomeIcon, FileText } from 'lucide-react';
import { formatCNPJ, onlyDigits } from '../utils/formatDocument';
import { EditableCell } from './EditableCell';
import MultiSelectCell from './shared/MultiSelectCell';
import EntityLink from './shared/EntityLink';
import { useLookupTables } from '../hooks/useLookupTables';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';

function EmpresaImoveisSection({ empresaId }) {
  const { data, loading, error } = useSupabaseQuery(
    (sb) => sb.from('v_empresa_imoveis').select('*').eq('empresa_id', empresaId),
    [empresaId]
  );

  if (loading) return null;
  if (error) {
    return <div className="text-[11px] text-red-600 px-1 py-2">Erro ao carregar imóveis: {error}</div>;
  }
  if (!data || data.length === 0) {
    return (
      <div className="mt-2 text-[11px] text-slate-500 italic">
        <HomeIcon size={11} className="inline mr-1" /> Esta empresa é landowner mas não tem imóveis vinculados.
      </div>
    );
  }

  const fmtArea = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 }) + ' m²');
  const fmtPct = (v) => (v == null ? '—' : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`);

  return (
    <div className="mt-3 border-t border-slate-100 pt-2">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 flex items-center gap-1.5">
        <HomeIcon size={11} className="text-sky-600" />
        🏠 Imóveis desta empresa ({data.length})
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="px-2 py-1.5 text-left">Imóvel</th>
              <th className="px-2 py-1.5 text-left">Matrícula</th>
              <th className="px-2 py-1.5 text-right">Área</th>
              <th className="px-2 py-1.5 text-right">%</th>
              <th className="px-2 py-1.5 text-left">Área de Negócio</th>
            </tr>
          </thead>
          <tbody>
            {data.map((r) => (
              <tr key={r.imovel_id} className="border-t border-slate-100 hover:bg-sky-50">
                <td className="px-2 py-1.5">
                  <EntityLink
                    entityType="imovel"
                    entityId={r.imovel_id}
                    label={r.imovel_nome || r.endereco || '—'}
                    showIcon={false}
                    className="max-w-[220px]"
                  />
                </td>
                <td className="px-2 py-1.5 font-mono text-slate-600 whitespace-nowrap">{r.matricula || '—'}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{fmtArea(r.area_terreno_m2)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-slate-600 whitespace-nowrap">{fmtPct(r.percentual_participacao)}</td>
                <td className="px-2 py-1.5 text-slate-600 truncate max-w-[140px]" title={r.area_nome || ''}>{r.area_nome || <span className="text-slate-400">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Painel de edição inline dos campos de Empresa na row expandida do Dashboard.
 * Todos os updates passam por onUpdate(empresaId, { campo: valor }) que no
 * Dashboard aciona useContacts.updateEmpresa (stampAudit automático).
 */
export default function EmpresaInlineEditPanel({ empresa, onUpdate }) {
  const { papel, statusOp, realEstate, setores } = useLookupTables();

  if (!empresa) return null;

  const save = (campo, valor) => onUpdate(empresa.id, { [campo]: valor });

  const Field = ({ label, icon: Icon, children, wide = false }) => (
    <div className={wide ? 'col-span-2' : ''}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1 mb-0.5">
        {Icon && <Icon size={10} />} {label}
      </div>
      <div className="text-xs text-slate-800">{children}</div>
    </div>
  );

  return (
    <div className="mb-3 px-3 py-2.5 bg-white border border-slate-200 rounded">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
        <Briefcase size={11} className="text-blue-600" />
        Empresa — dados editáveis
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-2">
        <Field label="Papel">
          <EditableCell
            type="select"
            value={empresa.papel_negocio || ''}
            options={[{ value: '', label: '—' }, ...papel]}
            onSave={(v) => save('papel_negocio', v || null)}
          />
        </Field>
        <Field label="Status">
          <EditableCell
            type="select"
            value={empresa.status_operacional || ''}
            options={[{ value: '', label: '—' }, ...statusOp]}
            onSave={(v) => save('status_operacional', v || null)}
          />
        </Field>
        <Field label="Tipo RE">
          <EditableCell
            type="select"
            value={empresa.real_estate_role || ''}
            options={[{ value: '', label: '—' }, ...realEstate]}
            onSave={(v) => save('real_estate_role', v || null)}
          />
        </Field>
        <Field label="Setores">
          <MultiSelectCell
            value={empresa.setores}
            options={setores}
            onSave={(arr) => save('setores', arr)}
          />
        </Field>

        <Field label="CNPJ" icon={FileText}>
          <EditableCell
            type="text"
            value={empresa.cnpj || ''}
            onSave={(v) => {
              const digits = onlyDigits(v);
              save('cnpj', digits || null);
            }}
            displayValue={empresa.cnpj ? formatCNPJ(empresa.cnpj) : undefined}
            placeholder="00.000.000/0001-00"
          />
        </Field>
        <Field label="Regiões" icon={MapPin}>
          <EditableCell
            type="tags"
            value={empresa.regiao_atuacao}
            onSave={(arr) => save('regiao_atuacao', arr)}
            placeholder="SP, RJ, MG"
          />
        </Field>
        <Field label="Website" icon={Globe}>
          <EditableCell
            type="text"
            value={empresa.website || ''}
            onSave={(v) => save('website', v || null)}
            placeholder="https://..."
          />
        </Field>
        <Field label="LinkedIn" icon={Linkedin}>
          <EditableCell
            type="text"
            value={empresa.company_linkedin_url || ''}
            onSave={(v) => save('company_linkedin_url', v || null)}
            placeholder="linkedin.com/company/..."
          />
        </Field>
        <Field label="Notas" icon={StickyNote} wide>
          <EditableCell
            type="textarea"
            value={empresa.notas_estrategicas || ''}
            onSave={(v) => save('notas_estrategicas', v || null)}
            placeholder="Notas estratégicas sobre esta empresa..."
          />
        </Field>
      </div>

      {empresa.papel_negocio === 'landowner' && (
        <EmpresaImoveisSection empresaId={empresa.id} />
      )}
    </div>
  );
}
